import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';

type Role = 'admin' | 'member';
interface AllowUser {
  email: string;
  role: Role;
  addedBy?: string;
  addedAt?: string;
}
interface UsersResponse {
  owners: string[];
  users: AllowUser[];
}

const ROLE_LABEL: Record<Role, string> = { admin: 'Admin', member: 'Mitglied' };

export function TeamPanel({ onClose }: { onClose: () => void }) {
  const { authedFetch, devMode } = useAuth();
  const [data, setData] = useState<UsersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('member');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authedFetch('/api/users');
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      setData((await res.json()) as UsersResponse);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Konnte Nutzer nicht laden.');
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    void load();
  }, [load]);

  const send = useCallback(
    async (method: 'POST' | 'PUT' | 'DELETE', body: Record<string, unknown>) => {
      setBusy(true);
      setError(null);
      try {
        const res = await authedFetch('/api/users', {
          method,
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        });
        const resp = (await res.json().catch(() => ({}))) as UsersResponse & { error?: string };
        if (!res.ok) throw new Error(resp.error || `HTTP ${res.status}`);
        setData(resp);
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Aktion fehlgeschlagen.');
        return false;
      } finally {
        setBusy(false);
      }
    },
    [authedFetch],
  );

  const onAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const addr = email.trim();
    if (!addr) return;
    if (await send('POST', { email: addr, role })) {
      setEmail('');
      setRole('member');
    }
  };

  return (
    <div className="cw-modal-backdrop" onClick={onClose}>
      <div className="cw-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cw-modal-head">
          <h2>Nutzer &amp; Berechtigungen</h2>
          <button className="cw-modal-close" onClick={onClose} aria-label="Schließen">
            ✕
          </button>
        </div>

        {devMode && (
          <div className="cw-modal-note">
            Dev-Modus: Nutzerverwaltung ist nur in der bereitgestellten App (mit Backend) aktiv.
          </div>
        )}

        <form className="cw-team-add" onSubmit={onAdd}>
          <input
            type="email"
            className="cw-team-input"
            placeholder="name@beispiel.de"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
          />
          <select
            className="cw-team-role"
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            disabled={busy}
          >
            <option value="member">Mitglied</option>
            <option value="admin">Admin</option>
          </select>
          <button className="cw-team-addbtn" type="submit" disabled={busy || !email.trim()}>
            Hinzufügen
          </button>
        </form>

        {error && <div className="cw-team-error">{error}</div>}
        {loading && <div className="cw-team-empty">Lädt …</div>}

        {data && (
          <div className="cw-team-list">
            {data.owners.map((o) => (
              <div className="cw-team-row" key={`owner-${o}`}>
                <span className="cw-team-email">{o}</span>
                <span className="cw-team-badge">Inhaber</span>
              </div>
            ))}
            {data.users.map((u) => (
              <div className="cw-team-row" key={u.email}>
                <span className="cw-team-email">{u.email}</span>
                <select
                  className="cw-team-role sm"
                  value={u.role}
                  disabled={busy}
                  onChange={(e) => send('PUT', { email: u.email, role: e.target.value })}
                  title="Rolle ändern"
                >
                  <option value="member">{ROLE_LABEL.member}</option>
                  <option value="admin">{ROLE_LABEL.admin}</option>
                </select>
                <button
                  className="cw-team-remove"
                  disabled={busy}
                  onClick={() => send('DELETE', { email: u.email })}
                  title="Zugriff entziehen"
                >
                  Entfernen
                </button>
              </div>
            ))}
            {data.users.length === 0 && (
              <div className="cw-team-empty">Noch keine zusätzlichen Nutzer freigeschaltet.</div>
            )}
          </div>
        )}

        <div className="cw-modal-foot">
          <strong>Inhaber</strong> werden serverseitig festgelegt (immer Admin, hier nicht
          änderbar). <strong>Admins</strong> können Nutzer verwalten; <strong>Mitglieder</strong>{' '}
          sehen nur das Dashboard.
        </div>
      </div>
    </div>
  );
}
